import { Component, AfterViewInit, ChangeDetectorRef, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { UserFormDialogComponent } from '../../components/user-form-dialog/user-form-dialog.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { SubscribeUserRequest, SubscriptionUserListItem, UserUpdate } from '../../models';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatInputModule, MatFormFieldModule, MatTableModule, MatSortModule, MatTooltipModule, MatDialogModule, RouterModule],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, AfterViewInit {
  displayedColumns = ['icon', 'userName', 'firstName', 'lastName', 'roleName', 'productName', 'activeYN', 'addedAt', 'actions'];
  searchQuery = '';
  dataSource = new MatTableDataSource<SubscriptionUserListItem>([]);

  @ViewChild('sortTable') sort!: MatSort;

  constructor(private dialog: MatDialog, private cdr: ChangeDetectorRef, private authService: AuthService, private dataService: DataService) {
    this.dataSource.filterPredicate = (user, filter) =>
      [user.userName, user.firstName, user.lastName, user.productName, user.roleName]
        .some(value => (value ?? '').toLowerCase().includes(filter));
    this.dataSource.sortingDataAccessor = (user, property) => {
      if (property === 'addedAt') return new Date(user.addedAt).getTime();
      return user[property as keyof SubscriptionUserListItem]?.toString().toLowerCase() ?? '';
    };
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  private loadUsers(): void {
    this.dataService.getUserList().subscribe(users => this.dataSource.data = users);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.sort.sort({ id: 'addedAt', start: 'desc', disableClear: false });
    this.cdr.detectChanges();
  }

  applyFilter(query: string): void { this.dataSource.filter = query.trim().toLowerCase(); }
  clearSearch(): void { this.searchQuery = ''; this.dataSource.filter = ''; }

  getSubscriptionRoleLabel(subscriptionRoleId: number): string {
    return subscriptionRoleId.toString();
  }

  getUserIcon(): string { return 'user'; }

  isCurrentUser(user: SubscriptionUserListItem): boolean {
    const currentUsername = this.authService.getCurrentUser()?.username;
    return !!currentUsername && user.userName.trim().toLowerCase() === currentUsername.trim().toLowerCase();
  }

  formatDate(date: string): string {
    const parsedDate = new Date(date);
    return parsedDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + parsedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  openUserDialog(user?: SubscriptionUserListItem): void {
    const ref = this.dialog.open(UserFormDialogComponent, { width: '620px', maxWidth: '92vw', data: { user } });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      if (user) {
        Object.assign(user, result as UserUpdate);
        this.dataSource.data = [...this.dataSource.data];
      } else {
        this.dataService.subscribeUser(result as SubscribeUserRequest).subscribe(() => this.loadUsers());
      }
    });
  }

  deleteUser(user: SubscriptionUserListItem): void {
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '460px', data: { title: 'Delete User', message: `Are you sure you want to delete ${user.userName}?`, confirmText: 'Delete', cancelText: 'Cancel' } });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dataService.deleteSubscribedUser(user.userName).subscribe(() => this.loadUsers());
      }
    });
  }
}